import { ArrowRight, Building2, CircleAlert, LifeBuoy, Loader2, ShieldCheck } from "lucide-react";
import type {
  PortalLoginActionViewModel,
  PortalLoginStatusViewModel,
  PortalLoginViewProps,
} from "../../contracts/tenant-identity";
import { cn } from "../../lib/utils";
import { Button } from "../../internal/primitives/button";

const statusClasses: Record<PortalLoginStatusViewModel["tone"], string> = {
  neutral: "border-border bg-muted/40 text-muted-foreground",
  info: "border-primary/30 bg-primary/10 text-foreground",
  success: "border-emerald-500/30 bg-emerald-500/10 text-foreground",
  warning: "border-amber-500/30 bg-amber-500/10 text-foreground",
  danger: "border-destructive/30 bg-destructive/10 text-foreground",
};

function ActionButton({
  action,
  variant,
}: {
  action: PortalLoginActionViewModel;
  variant: "default" | "outline" | "ghost";
}) {
  return (
    <Button
      type="button"
      variant={variant}
      size="lg"
      className="h-auto min-h-11 w-full justify-between gap-4 whitespace-normal px-4 py-3 text-left"
      disabled={action.disabled || action.pending}
      onClick={action.onInvoke}
    >
      <span className="min-w-0">
        <span className="block font-bold">{action.label}</span>
        {action.description ? (
          <span className={cn("mt-0.5 block text-xs font-normal", variant === "default" ? "text-primary-foreground/80" : "text-muted-foreground")}>
            {action.description}
          </span>
        ) : null}
      </span>
      {action.pending ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" /> : <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />}
    </Button>
  );
}

export function PortalLoginView({
  mode,
  productName,
  tenantName,
  tenantLogoUrl,
  eyebrow,
  title,
  description,
  status,
  primaryAction,
  secondaryAction,
  registrationAction,
  supportAction,
}: PortalLoginViewProps) {
  const identityLabel = mode === "tenant" && tenantName ? tenantName : productName;

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-10 text-foreground sm:px-6">
      <section className="w-full max-w-md" aria-labelledby="portal-login-title">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-panel border border-border bg-card">
            {tenantLogoUrl ? (
              <img src={tenantLogoUrl} alt="" className="h-full w-full object-contain p-1.5" />
            ) : mode === "tenant" ? (
              <Building2 className="h-5 w-5 text-primary" aria-hidden="true" />
            ) : (
              <ShieldCheck className="h-5 w-5 text-primary" aria-hidden="true" />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">{eyebrow}</p>
            <p className="truncate text-sm font-bold">{identityLabel}</p>
          </div>
        </div>

        <div className="rounded-panel border border-border bg-card shadow-sm">
          <header className="border-b border-border px-5 py-5 sm:px-6">
            <h1 id="portal-login-title" className="text-2xl font-black tracking-tight">{title}</h1>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>
          </header>

          <div className="space-y-3 px-5 py-5 sm:px-6">
            {status ? (
              <div className={cn("flex gap-3 rounded-control border px-3 py-3", statusClasses[status.tone])} role={status.tone === "danger" ? "alert" : "status"} aria-live="polite">
                <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-sm font-bold">{status.title}</p>
                  <p className="mt-0.5 text-xs leading-5 text-muted-foreground">{status.message}</p>
                </div>
              </div>
            ) : null}

            <ActionButton action={primaryAction} variant="default" />
            {secondaryAction ? <ActionButton action={secondaryAction} variant="outline" /> : null}

            {registrationAction ? (
              <div className="border-t border-border pt-3 text-center">
                <Button type="button" variant="link" onClick={registrationAction.onInvoke} disabled={registrationAction.disabled || registrationAction.pending}>
                  {registrationAction.label}
                </Button>
              </div>
            ) : null}
          </div>
        </div>

        {supportAction ? (
          <div className="mt-4 flex justify-center">
            <Button type="button" variant="ghost" size="sm" className="gap-2 text-muted-foreground" onClick={supportAction.onInvoke} disabled={supportAction.disabled || supportAction.pending}>
              <LifeBuoy className="h-4 w-4" aria-hidden="true" />
              {supportAction.label}
            </Button>
          </div>
        ) : null}
      </section>
    </main>
  );
}
